import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Brain,
  Layers,
  ScanLine,
  Target,
  Cpu,
  MemoryStick,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { MONAIBundle, MONAITaskType, MONAIModality } from '@/mock/gpu-instance';

interface MONAIBundleSelectorProps {
  bundles: MONAIBundle[];
  onSelectBundle?: (bundle: MONAIBundle) => void;
  onViewBundleCard?: (bundle: MONAIBundle) => void;
  selectedBundleId?: string;
  filterTask?: MONAITaskType;
  filterModality?: MONAIModality;
}

const getTaskIcon = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return <Layers className="h-4 w-4 text-purple-500" />;
    case 'classification':
      return <ScanLine className="h-4 w-4 text-blue-500" />;
    case 'detection':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'registration':
      return <Brain className="h-4 w-4 text-orange-500" />;
  }
};

const getTaskBadge = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <Layers className="h-3 w-3 mr-1" /> Segmentation
        </Badge>
      );
    case 'classification':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <ScanLine className="h-3 w-3 mr-1" /> Classification
        </Badge>
      );
    case 'detection':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Target className="h-3 w-3 mr-1" /> Detection
        </Badge>
      );
    case 'registration':
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          <Brain className="h-3 w-3 mr-1" /> Registration
        </Badge>
      );
  }
};

const getModalityBadge = (modality: MONAIModality) => {
  const colors: Record<MONAIModality, string> = {
    CT: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    MRI: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    'X-Ray': 'bg-cyan-500/10 text-cyan-600 border-cyan-500/30',
    Ultrasound: 'bg-green-500/10 text-green-600 border-green-500/30',
    Pathology: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
    Fundus: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    Dermoscopy: 'bg-red-500/10 text-red-600 border-red-500/30',
    Endoscopy: 'bg-teal-500/10 text-teal-600 border-teal-500/30',
  };
  return <Badge variant="outline" className={colors[modality]}>{modality}</Badge>;
};

const getMetricColor = (value: number) => {
  if (value >= 0.9) return 'text-green-600';
  if (value >= 0.8) return 'text-yellow-600';
  return 'text-orange-600';
};

const MONAIBundleSelector = ({
  bundles,
  onSelectBundle,
  onViewBundleCard,
  selectedBundleId,
  filterTask: initialFilterTask,
  filterModality: initialFilterModality,
}: MONAIBundleSelectorProps) => {
  const [filterTask, setFilterTask] = useState<string>(initialFilterTask || 'all');
  const [filterModality, setFilterModality] = useState<string>(initialFilterModality || 'all');
  const [sortBy, setSortBy] = useState<string>('accuracy');

  const filteredBundles = bundles
    .filter((bundle) => {
      if (filterTask !== 'all' && bundle.task !== filterTask) return false;
      if (filterModality !== 'all' && bundle.modality !== filterModality) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'accuracy':
          const aScore = a.diceScore || a.accuracy || a.auc || 0;
          const bScore = b.diceScore || b.accuracy || b.auc || 0;
          return bScore - aScore;
        case 'vram':
          return a.vramRequired - b.vramRequired;
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterTask} onValueChange={setFilterTask}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by task" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="segmentation">Segmentation</SelectItem>
            <SelectItem value="classification">Classification</SelectItem>
            <SelectItem value="detection">Detection</SelectItem>
            <SelectItem value="registration">Registration</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterModality} onValueChange={setFilterModality}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by modality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modalities</SelectItem>
            <SelectItem value="CT">CT</SelectItem>
            <SelectItem value="MRI">MRI</SelectItem>
            <SelectItem value="X-Ray">X-Ray</SelectItem>
            <SelectItem value="Ultrasound">Ultrasound</SelectItem>
            <SelectItem value="Pathology">Pathology</SelectItem>
            <SelectItem value="Fundus">Fundus</SelectItem>
            <SelectItem value="Dermoscopy">Dermoscopy</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="accuracy">Highest Accuracy</SelectItem>
            <SelectItem value="vram">Lowest VRAM</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bundle Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredBundles.map((bundle) => (
          <Card
            key={bundle.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              selectedBundleId === bundle.id ? 'border-primary ring-1 ring-primary' : ''
            }`}
            onClick={() => onViewBundleCard?.(bundle)}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-red-500/10 to-pink-500/10">
                    {getTaskIcon(bundle.task)}
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {bundle.displayName}
                      {selectedBundleId === bundle.id && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {bundle.description}
                    </div>
                  </div>
                </div>
              </div>

              {/* Task & Modality */}
              <div className="flex items-center gap-2 mb-3">
                {getTaskBadge(bundle.task)}
                {getModalityBadge(bundle.modality)}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">
                    {bundle.diceScore ? 'Dice' : bundle.accuracy ? 'Acc' : 'AUC'}
                  </div>
                  <div className={`font-mono font-bold text-sm ${getMetricColor(bundle.diceScore || bundle.accuracy || bundle.auc || 0)}`}>
                    {((bundle.diceScore || bundle.accuracy || bundle.auc || 0) * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <MemoryStick className="h-3 w-3" />
                    VRAM
                  </div>
                  <div className="font-mono font-medium text-sm">
                    {bundle.vramRequired}GB
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Cpu className="h-3 w-3" />
                    Arch
                  </div>
                  <div className="font-mono font-medium text-xs truncate">
                    {bundle.architecture}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Input</div>
                  <div className="font-mono font-medium text-xs truncate">
                    {bundle.inputSize}
                  </div>
                </div>
              </div>

              {/* NGC Path */}
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <code className="text-xs text-muted-foreground truncate">
                  {bundle.ngcPath}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`https://catalog.ngc.nvidia.com/orgs/nvidia/teams/monailabel/models/${bundle.name}`, '_blank');
                  }}
                  title="View on NGC Catalog"
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredBundles.length === 0 && (
          <Card className="col-span-2 border-dashed">
            <CardContent className="pt-6 text-center">
              <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No bundles match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MONAIBundleSelector;
